import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Card, CardContent } from "@/components/ui/card";
import { Bell, Plus } from "lucide-react";
import { toast } from "sonner";

const TABS = [
  { k: "invoices", l: "Invoices" },
  { k: "aging", l: "Aging" },
  { k: "ledger", l: "Customer ledger" },
  { k: "payments", l: "Payments" },
];

function fmt(n) { return `₹${Number(n || 0).toLocaleString()}`; }

function bucketAging(daysOverdue) {
  if (daysOverdue <= 30) return "b0_30";
  if (daysOverdue <= 60) return "b31_60";
  if (daysOverdue <= 90) return "b61_90";
  if (daysOverdue <= 180) return "b91_180";
  return "b180_plus";
}

/**
 * Distributor Collections — same shape as Finance (invoices/aging/ledger/payments)
 * but sourced from dispatch orders. One row per dispatch → shows customer, dispatch #,
 * invoice #, amount, and payment status computed from linked sales-invoice.
 */
export default function DistributorCollections({ title = "Collections", subtitle = "Every dispatched order and its collection status — one dispatch per row, aggregated for aging and ledger views." }) {
  const [tab, setTab] = useState("invoices");
  const [dispatches, setDispatches] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [payments, setPayments] = useState([]);
  const [customerFilter, setCustomerFilter] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const EMPTY_FORM = { customer_name: "", customer_email: "", method: "NEFT", amount: "", reference: "", date: new Date().toISOString().slice(0, 10), notes: "", invoice_id: "" };
  const [form, setForm] = useState(EMPTY_FORM);

  const reload = () => {
    Promise.all([
      api.get("/org/dispatches").catch(() => ({ data: [] })),
      api.get("/finance/sales-invoices").catch(() => ({ data: [] })),
      api.get("/finance/payments").catch(() => ({ data: [] })),
    ]).then(([d, i, p]) => {
      setDispatches(d.data || []); setInvoices(i.data || []); setPayments(p.data || []);
    });
  };

  useEffect(() => { reload(); }, []);

  // Merge dispatches with linked sales-invoice for financials
  const rows = useMemo(() => {
    const invByDispatchNum = Object.fromEntries((invoices || []).map((i) => [i.dispatch_number || i.number, i]));
    return (dispatches || []).map((d) => {
      const inv = invByDispatchNum[d.number] || invByDispatchNum[d.invoice_number] || null;
      const amount = Number(d.total_value || d.total || inv?.total || 0);
      const outstanding = Number(inv?.outstanding ?? amount);
      const dueDate = inv?.due_date ? new Date(inv.due_date) : null;
      const daysOverdue = dueDate ? Math.max(0, Math.floor((Date.now() - dueDate.getTime()) / 86400000)) : 0;
      return {
        id: d.id,
        dispatch_number: d.number,
        invoice_number: d.invoice_number || inv?.number,
        customer_name: d.customer_name,
        customer_email: d.customer_email || inv?.customer_email,
        sales_order_number: d.sales_order_number,
        amount,
        outstanding,
        due_date: inv?.due_date,
        status: inv?.status || (outstanding > 0 ? "OPEN" : "PAID"),
        days_overdue: daysOverdue,
      };
    });
  }, [dispatches, invoices]);

  const totalDue = useMemo(() => rows.reduce((s, r) => s + r.outstanding, 0), [rows]);
  const totalBilled = useMemo(() => rows.reduce((s, r) => s + r.amount, 0), [rows]);

  const remind = async (r) => {
    if (!r.invoice_number) return toast.error("No invoice attached yet — cannot remind.");
    try {
      // Best-effort — server may have a lookup by invoice number; else silently ignore
      const inv = invoices.find((i) => (i.number || i.invoice_number) === r.invoice_number);
      const invoice_id = inv?.id;
      await api.post("/finance/reminders", { invoice_id, level: 1, note: `Outstanding ₹${r.outstanding.toLocaleString()} on ${r.dispatch_number}` });
      toast.success(`Reminder sent to ${r.customer_name}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const openAdd = (row) => {
    setForm({
      ...EMPTY_FORM,
      customer_name: row?.customer_name || "",
      customer_email: row?.customer_email || "",
      amount: row?.outstanding ? String(row.outstanding) : "",
      invoice_id: row ? (invoices.find((i) => (i.number || i.invoice_number) === row.invoice_number)?.id || "") : "",
      reference: row?.invoice_number ? `Against ${row.invoice_number}` : "",
    });
    setAddOpen(true);
  };

  const submitReceipt = async () => {
    if (!form.customer_name.trim()) return toast.error("Customer name is required");
    if (!form.customer_email.trim()) return toast.error("Customer email is required");
    const amt = Number(form.amount);
    if (!amt || amt <= 0) return toast.error("Amount must be greater than 0");
    setSaving(true);
    try {
      const body = {
        customer_name: form.customer_name.trim(),
        customer_email: form.customer_email.trim(),
        method: form.method,
        amount: amt,
        reference: form.reference.trim() || null,
        date: form.date ? new Date(form.date).toISOString() : null,
        notes: form.notes.trim() || null,
        allocations: form.invoice_id ? [{ invoice_id: form.invoice_id, amount: amt }] : [],
      };
      await api.post("/finance/payments", body);
      toast.success("Receipt recorded");
      setAddOpen(false);
      setForm(EMPTY_FORM);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to record receipt");
    } finally {
      setSaving(false);
    }
  };

  const aging = useMemo(() => {
    const byCust = {};
    rows.filter((r) => r.outstanding > 0).forEach((r) => {
      const key = r.customer_email || r.customer_name || "—";
      if (!byCust[key]) byCust[key] = { customer_name: r.customer_name, customer_email: r.customer_email, b0_30: 0, b31_60: 0, b61_90: 0, b91_180: 0, b180_plus: 0, total: 0 };
      const b = bucketAging(r.days_overdue);
      byCust[key][b] += r.outstanding;
      byCust[key].total += r.outstanding;
    });
    return Object.values(byCust).sort((a, b) => b.total - a.total);
  }, [rows]);

  const ledger = useMemo(() => {
    if (!customerFilter) return [];
    const f = customerFilter.toLowerCase();
    const entries = [];
    let bal = 0;
    rows.filter((r) => (r.customer_email || "").toLowerCase() === f || (r.customer_name || "").toLowerCase().includes(f))
        .forEach((r) => {
          bal += r.amount;
          entries.push({ id: `d-${r.id}`, date: r.due_date, ref: r.dispatch_number, type: "DISPATCH", debit: r.amount, credit: 0, running: bal });
          const paid = r.amount - r.outstanding;
          if (paid > 0) {
            bal -= paid;
            entries.push({ id: `p-${r.id}`, date: r.due_date, ref: r.invoice_number, type: "PAYMENT", debit: 0, credit: paid, running: bal });
          }
        });
    return entries;
  }, [rows, customerFilter]);

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6" data-testid="dist-collections">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-black">{title}</h1>
          <p className="text-muted-foreground">{subtitle}</p>
        </div>
        <div className="flex items-start gap-3 flex-wrap">
          <Button onClick={() => openAdd(null)} data-testid="dist-coll-add-btn" className="mt-1">
            <Plus className="w-4 h-4 mr-1" /> Record receipt
          </Button>
          <div className="grid grid-cols-2 gap-3">
            <div className="p-3 rounded-xl border border-border bg-card min-w-[180px]" data-testid="dist-coll-total-billed">
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold">Total billed</div>
              <div className="font-display text-2xl font-black text-blue-700">{fmt(totalBilled)}</div>
            </div>
            <div className="p-3 rounded-xl border border-border bg-card min-w-[180px]" data-testid="dist-coll-total-due">
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold">Outstanding</div>
              <div className="font-display text-2xl font-black text-orange-600">{fmt(totalDue)}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex gap-2 border-b border-border">
        {TABS.map((t) => (
          <button key={t.k} onClick={() => setTab(t.k)}
                  className={`px-3 py-2 text-sm font-semibold ${tab === t.k ? "border-b-2 border-primary text-foreground" : "text-muted-foreground hover:text-foreground"}`}
                  data-testid={`dist-coll-tab-${t.k}`}>{t.l}</button>
        ))}
      </div>

      {tab === "invoices" && (
        <Card><CardContent className="p-4 overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Dispatch #</TableHead>
              <TableHead>Invoice #</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead>SO #</TableHead>
              <TableHead>Amount</TableHead>
              <TableHead>Outstanding</TableHead>
              <TableHead>Due</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} data-testid={`dist-coll-row-${r.id}`}>
                  <TableCell className="font-mono text-xs">{r.dispatch_number}</TableCell>
                  <TableCell className="font-mono text-xs">{r.invoice_number || "—"}</TableCell>
                  <TableCell className="text-sm">{r.customer_name}</TableCell>
                  <TableCell className="font-mono text-xs">{r.sales_order_number || "—"}</TableCell>
                  <TableCell className="font-mono font-semibold">{fmt(r.amount)}</TableCell>
                  <TableCell className="font-mono">{fmt(r.outstanding)}</TableCell>
                  <TableCell className="text-xs">{r.due_date ? new Date(r.due_date).toLocaleDateString() : "—"}</TableCell>
                  <TableCell><Badge variant="outline" className="font-mono text-[10px]">{r.status}</Badge></TableCell>
                  <TableCell className="text-right">
                    {r.outstanding > 0 ? (
                      <div className="flex gap-2 justify-end">
                        <Button size="sm" variant="outline" onClick={() => remind(r)} data-testid={`dist-coll-remind-${r.id}`}>
                          <Bell className="w-3 h-3 mr-1" /> Remind
                        </Button>
                        <Button size="sm" onClick={() => openAdd(r)} data-testid={`dist-coll-receive-${r.id}`}>
                          <Plus className="w-3 h-3 mr-1" /> Record
                        </Button>
                      </div>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && <TableRow><TableCell colSpan={9} className="text-center text-muted-foreground py-8">No dispatch orders yet.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent></Card>
      )}

      {tab === "aging" && (
        <Card><CardContent className="p-4 overflow-x-auto">
          <Table>
            <TableHeader><TableRow><TableHead>Customer</TableHead><TableHead>0-30</TableHead><TableHead>31-60</TableHead><TableHead>61-90</TableHead><TableHead>91-180</TableHead><TableHead>180+</TableHead><TableHead>Total</TableHead></TableRow></TableHeader>
            <TableBody>
              {aging.map((a, ix) => (
                <TableRow key={ix}>
                  <TableCell><div className="text-sm font-medium">{a.customer_name}</div><div className="text-xs text-muted-foreground">{a.customer_email}</div></TableCell>
                  <TableCell className="font-mono">{fmt(a.b0_30)}</TableCell>
                  <TableCell className="font-mono">{fmt(a.b31_60)}</TableCell>
                  <TableCell className="font-mono">{fmt(a.b61_90)}</TableCell>
                  <TableCell className="font-mono">{fmt(a.b91_180)}</TableCell>
                  <TableCell className="font-mono">{fmt(a.b180_plus)}</TableCell>
                  <TableCell className="font-mono font-bold">{fmt(a.total)}</TableCell>
                </TableRow>
              ))}
              {aging.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">Nothing outstanding.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent></Card>
      )}

      {tab === "ledger" && (
        <div className="space-y-3">
          <input
            type="text"
            placeholder="Type customer email or name…"
            value={customerFilter}
            onChange={(e) => setCustomerFilter(e.target.value)}
            data-testid="dist-coll-ledger-search"
            className="w-full max-w-md rounded-lg border border-border bg-card px-3 py-2 text-sm"
          />
          <Card><CardContent className="p-4 overflow-x-auto">
            <Table>
              <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Reference</TableHead><TableHead>Type</TableHead><TableHead>Debit</TableHead><TableHead>Credit</TableHead><TableHead>Balance</TableHead></TableRow></TableHeader>
              <TableBody>
                {ledger.map((e) => (
                  <TableRow key={e.id}>
                    <TableCell className="text-xs">{e.date ? new Date(e.date).toLocaleDateString() : "—"}</TableCell>
                    <TableCell className="font-mono text-xs">{e.ref}</TableCell>
                    <TableCell><Badge variant="outline" className="font-mono text-[10px]">{e.type}</Badge></TableCell>
                    <TableCell className="font-mono">{e.debit ? fmt(e.debit) : "—"}</TableCell>
                    <TableCell className="font-mono">{e.credit ? fmt(e.credit) : "—"}</TableCell>
                    <TableCell className="font-mono font-bold">{fmt(e.running)}</TableCell>
                  </TableRow>
                ))}
                {ledger.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">Type a customer name or email above.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </CardContent></Card>
        </div>
      )}

      {tab === "payments" && (
        <Card><CardContent className="p-4 overflow-x-auto space-y-3">
          <div className="flex justify-end">
            <Button size="sm" onClick={() => openAdd(null)} data-testid="dist-coll-payments-add-btn">
              <Plus className="w-4 h-4 mr-1" /> Add payment record
            </Button>
          </div>
          <Table>
            <TableHeader><TableRow><TableHead>Number</TableHead><TableHead>Customer</TableHead><TableHead>Method</TableHead><TableHead>Amount</TableHead><TableHead>Ref</TableHead><TableHead>Date</TableHead></TableRow></TableHeader>
            <TableBody>
              {payments.map((p) => (
                <TableRow key={p.id}>
                  <TableCell className="font-mono text-xs">{p.number}</TableCell>
                  <TableCell className="text-sm">{p.customer_name}</TableCell>
                  <TableCell><Badge variant="outline" className="font-mono text-[10px]">{p.method}</Badge></TableCell>
                  <TableCell className="font-mono">{fmt(p.amount)}</TableCell>
                  <TableCell className="text-xs font-mono">{p.reference || "—"}</TableCell>
                  <TableCell className="text-xs">{p.date ? new Date(p.date).toLocaleDateString() : "—"}</TableCell>
                </TableRow>
              ))}
              {payments.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">No payments recorded yet.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent></Card>
      )}

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-lg" data-testid="dist-coll-add-dialog">
          <DialogHeader><DialogTitle>Record receipt</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label htmlFor="rcpt-name">Customer name *</Label>
              <Input id="rcpt-name" value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} data-testid="rcpt-name" placeholder="Acme Ceramics" />
            </div>
            <div>
              <Label htmlFor="rcpt-email">Customer email *</Label>
              <Input id="rcpt-email" type="email" value={form.customer_email} onChange={(e) => setForm({ ...form, customer_email: e.target.value })} data-testid="rcpt-email" placeholder="buyer@example.com" />
            </div>
            <div>
              <Label>Payment method</Label>
              <Select value={form.method} onValueChange={(v) => setForm({ ...form, method: v })}>
                <SelectTrigger data-testid="rcpt-method"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["NEFT", "RTGS", "IMPS", "BANK_TRANSFER", "CHEQUE", "CASH"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="rcpt-amount">Amount (₹) *</Label>
              <Input id="rcpt-amount" type="number" min="0" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="rcpt-amount" />
            </div>
            <div>
              <Label htmlFor="rcpt-ref">Reference / UTR / Cheque #</Label>
              <Input id="rcpt-ref" value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} data-testid="rcpt-ref" placeholder="UTR / cheque #" />
            </div>
            <div>
              <Label htmlFor="rcpt-date">Date</Label>
              <Input id="rcpt-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="rcpt-date" />
            </div>
            <div className="sm:col-span-2">
              <Label>Apply to invoice (optional)</Label>
              <Select value={form.invoice_id || "none"} onValueChange={(v) => setForm({ ...form, invoice_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="rcpt-invoice"><SelectValue placeholder="— No allocation —" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— No allocation —</SelectItem>
                  {invoices.filter((i) => (i.outstanding ?? i.total ?? 0) > 0).map((i) => (
                    <SelectItem key={i.id} value={i.id}>{i.number || i.invoice_number} · {i.customer_name} · {fmt(i.outstanding ?? i.total)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="rcpt-notes">Notes</Label>
              <Input id="rcpt-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="rcpt-notes" placeholder="Optional remarks" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)} data-testid="rcpt-cancel">Cancel</Button>
            <Button onClick={submitReceipt} disabled={saving} data-testid="rcpt-submit">{saving ? "Saving…" : "Record receipt"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
